import { useEffect, useState } from 'react';
import { ArrowLeftOutlined, DeleteOutlined, PlusOutlined } from '@ant-design/icons';
import { Typography, Flex, Input, Select, Button, Divider, Collapse, message, Tooltip, ColorPicker } from 'antd';
import { findWidgets, getAllWidgets } from '../../../utils/formsHelper';
import { comparatorData } from '../../../utils/comparatorData';
import ConditionValue from './ConditionValue';
import ReferenceDropdown from './ReferenceDropdown';
import SetValue from './SetValue';
import { useTranslation } from 'react-i18next';

// Their inputs don't read the conditional "set" value, so offering them as a
// Set target only saves a condition that silently does nothing at render time.
const nonSettableFormTypes = ["attachment", "imageForm", "tableForm", "ratingScaleField"]

const AddEditConditionForm = ({ setShowForm, selectedCondition, saveConditions, handleCancel, formElements, workflowSteps }) => {
    const { t } = useTranslation();

    const operators = [
        { label: t('AND'), value: '&&' },
        { label: t('OR'), value: '||' }
    ]

    const condtionActions = [
        { label: t("Show"), value: "show" },
        { label: t("Hide"), value: "hide" },
        { label: t("Mandatory"), value: "mandatory" },
        { label: t("Editable"), value: "editable" },
        { label: t("Set"), value: "set" },
        { label: t("Set Cell Color"), value: "setCellColor" },
        { label: t("Highlight"), value: "highlight" }
    ]
    const [data, setData] = useState(
        {
            name: '',
            scenarios: [
                {
                    operator: "",
                    conditions: [
                        { reference: undefined, comparator: undefined, value: "" },
                    ]
                }
            ]
        }
    )
    const [actions, setActions] = useState([{ action: undefined, field: undefined }])

    let formFields = getAllWidgets(formElements)
    // let formFields = findWidgets(formElements, "", { includeSection: true }) 
    formFields = formFields.map(field => {
        let reqLabel = field.label || field.name || t('Untitled Field')
        if (field.sectionLabel) {
            reqLabel += " (" + field.sectionLabel + ")"
        }
        if (field.formLabel) {
            reqLabel += " - " + field.formLabel
        }
        return { ...field, label: reqLabel, value: field.id }
    })

    useEffect(() => {
        if (selectedCondition.conditionId) {
            setData({ ...selectedCondition })
            // Unpack actions where column ID might be packed into the value
            const loadedActions = (selectedCondition.actions || []).map(action => {
                if (action.action === 'setCellColor' && action.value && typeof action.value === 'string' && action.value.includes('::')) {
                    const [color, column] = action.value.split('::');
                    return { ...action, value: color, column: column };
                }
                return action;
            });
            setActions(loadedActions)
        }
    }, [selectedCondition])

    const handleChange = (e) => {
        const { value } = e.target
        setData(prevData => ({ ...prevData, name: value }))
    }

    const handleScenarioChange = (key, val, scenarioIndex, conditionIndex) => {
        setData(prevData => {
            const updatedScenarios = prevData.scenarios.map((scenario, index) => {
                if (scenarioIndex !== index) {
                    return { ...scenario }
                }

                if (conditionIndex === undefined) {
                    return { ...scenario, [key]: val }
                }

                const reqConditions = scenario.conditions.map((condition, conditionIdx) => {
                    if (conditionIdx !== conditionIndex) {
                        return { ...condition }
                    }

                    if (key === "parent") {
                        if (val === "Form field" || val === "Score") {
                            return { parent: val }
                        }
                        return { parent: val, reference: val }
                    }

                    if (key === "formReference") {
                        return { parent: val[0], reference: val[1] }
                    }

                    if (key === "scoreReference") {
                        return { parent: "Score", scoreType: val[0], reference: val[1] }
                    }

                    if (key === "reference") {
                        return { [key]: val, parent: condition.parent, ...(condition.scoreType ? { scoreType: condition.scoreType } : {}) }
                    }

                    if (key === "comparator") {
                        return { reference: condition.reference, [key]: val, parent: condition.parent, ...(condition.scoreType ? { scoreType: condition.scoreType } : {}) }
                    }

                    return { ...condition, [key]: val }
                })

                return { ...scenario, conditions: reqConditions }
            })

            return { ...prevData, scenarios: updatedScenarios }
        })
    }

    const addCondition = (scenarioIndex) => {
        setData(prevData => {
            const updatedScenarios = prevData.scenarios.map((scenario, index) => {
                if (scenarioIndex !== index) {
                    return { ...scenario }
                }

                const reqConditions = [...scenario.conditions, { reference: "", comparator: "", value: "" }]
                return { ...scenario, conditions: reqConditions, operator: "&&" }
            })

            return { ...prevData, scenarios: updatedScenarios }
        })
    }

    const deleteCondition = (scenarioIndex, conditionIndex) => {
        setData(prevData => {
            const updatedScenarios = prevData.scenarios
                .map((scenario, index) => {
                    if (scenarioIndex !== index) {
                        return { ...scenario }
                    }

                    const reqConditions = scenario.conditions.filter((condition, idx) => idx !== conditionIndex)
                    if (conditionIndex > 0 && reqConditions[conditionIndex - 1]) {
                        reqConditions[conditionIndex - 1] = { ...reqConditions[conditionIndex - 1], operator: "" }
                    }

                    return { ...scenario, conditions: reqConditions }
                })
                .filter(scenario => scenario.conditions.length > 0)

            return { ...prevData, scenarios: updatedScenarios }
        })
    }

    const addScenario = () => {
        setData(prevData => ({
            ...prevData,
            scenarios: prevData.scenarios.concat({
                operator: "&&",
                conditions: [
                    { reference: undefined, comparator: undefined, value: "" }
                ]
            })
        }))
    }

    const submitConditions = () => {
        if (!data.name) {
            message.error(t("Condition name is missing"))
            return
        }

        if (!data.scenarios || data.scenarios.length === 0) {
            message.error(t("At least one scenario is required"))
            return
        }

        if (!actions || actions.length === 0 || !actions.some(a => a.action)) {
            message.error(t("At least one action is required"))
            return
        }

        try {
            // Validate actions and pack column ID into value for persistence
            const actionsToSave = actions.map((action, i) => {
                if (action.action === "setCellColor") {
                    if (!action.field) {
                        message.error(`${t("Action")} ${i + 1}: ${t("Please select a table field")}`);
                        throw new Error("Validation Failed");
                    }
                    if (!action.column) {
                        message.error(`${t("Action")} ${i + 1}: ${t("Please select a column for table")} ${action.field}`);
                        throw new Error("Validation Failed");
                    }
                    if (!action.value) {
                        message.error(`${t("Action")} ${i + 1}: ${t("Please select a color")}`);
                        throw new Error("Validation Failed");
                    }
                    // Pack column into value: "color::columnId"
                    return { ...action, value: `${action.value}::${action.column}` };
                }
                if (action.action === "highlight") {
                    if (!action.field) {
                        message.error(`${t("Action")} ${i + 1}: ${t("Please select a section or field")}`);
                        throw new Error("Validation Failed");
                    }
                    if (!action.value) {
                        message.error(`${t("Action")} ${i + 1}: ${t("Please select a color")}`);
                        throw new Error("Validation Failed");
                    }
                }
                if (action.action === "set" && action.valueSource === "dynamic" && !action.dynamicField) {
                    message.error(`${t("Action")} ${i + 1}: ${t("Please select a source field for dynamic value")}`);
                    throw new Error("Validation Failed");
                }
                return action;
            });

            saveConditions({ ...data, actions: actionsToSave })
        } catch (e) {
            // Validation error, do nothing (message already shown via message.error)
        }
    }

    const handleActionChange = (val, field, index) => {
        let actionsToBeChanged = [...actions]
        actionsToBeChanged = actionsToBeChanged.map((action, ind) => {
            if (index === ind) {
                if (field === "field") {
                    return { ...action, [field]: val, value: null, column: null, dynamicField: null }
                }
                if (field === "action" && (val === "setCellColor" || val === "highlight")) {
                    return { ...action, [field]: val, value: action.value || "#1677ff" }
                }
                return { ...action, [field]: val }
            }
            return { ...action }
        })
        setActions(actionsToBeChanged)
    }

    const addPositiveAction = () => {
        let updatedActions = [...actions]
        updatedActions.push({ action: undefined, field: undefined })
        setActions(updatedActions)
    }

    const deletePositiveAction = (index) => {
        let updatedActions = actions.filter((action, ind) => ind !== index)
        setActions(updatedActions)
    }

    const translateComparators = (comparators) => {
        return (comparators || []).map(comp => ({ ...comp, label: t(comp.label) }))
    }

    const getComparators = (reference, parent) => {

        if (parent === "Workflow step" || parent === "User" || parent === "Group") {
            return translateComparators(comparatorData["workflowStep"])
        }
        if (parent === "connectedUser") { return translateComparators(comparatorData["connectedUser"]) }
        if (parent === "Score") { return translateComparators(comparatorData["numberForm"]) }

        // Handle field::option format - extract the field ID
        let refObj = formFields.find(field => field.id === reference)
        if (!refObj && reference && reference.includes("::")) {
            let fieldId = reference.split("::")[0]
            refObj = formFields.find(field => field.id === fieldId)
        }
        if (refObj) {
            return translateComparators(comparatorData[refObj.formType])
        }
        return []
    }

    const getFormType = (reference, parent) => {
        let refObj = {}
        if (parent === "Workflow step") {
            refObj.formType = "dropdownField"

            refObj.options = workflowSteps
            return refObj

        } else if (parent === "Score") {
            return { formType: "numberForm" }
        } else {
            // Try to find the field by full reference first (handles tableId::colId)
            let refObj = formFields.find(field => field.id === reference)

            // Fallback for legacy field::option
            if (!refObj && reference && reference.includes("::")) {
                let fieldId = reference.split("::")[0]
                refObj = formFields.find(field => field.id === fieldId)
            }

            if (refObj) {
                return refObj
            }
        }
        return { formType: "inputForm" }
    }

    const setElementValue = (val, actionIndex, field = "value") => {
        setActions(prevActions => {
            let updatedActions = prevActions.map((action, index) => {
                if (index === actionIndex) {
                    return { ...action, [field]: val }
                }
                return { ...action }
            })
            return updatedActions
        })
    }

    const getActionFields = (action) => {
        let actionFields = [...formFields]
        if (action === "setCellColor") {
            // Cell colour only ever applies to a table, so this list already leaves
            // out every type that can't take a value.
            return actionFields.filter(field => field.formType === "tableForm")
        }
        // "highlight" targets anything on the form - every section and every field,
        // whether or not scoring is enabled on it - so no filtering happens here and
        // sections are deliberately left in the list below.
        if (action === "set") {
            actionFields = actionFields.filter(field => !nonSettableFormTypes.includes(field.formType))
        }
        if (action !== "show" && action !== "hide" && action !== "highlight") {
            actionFields = actionFields.filter(field => field.formType !== "section")
        }
        actionFields = actionFields.map(field => {
            let reqLabel = field.label || field.name || t('Untitled Field')
            // if (field.sectionLabel) {
            //     reqLabel += " (" + field.sectionLabel + ")"
            // }
            if (field.formType === "infoForm" && field.headingLabel) {
                reqLabel += " (" + field.headingLabel + ")"
            }
            return { label: <Tooltip title={reqLabel}>{reqLabel}</Tooltip>, actualLabel: field.label || field.name || t('Untitled Field'), value: field.value }
        })
        return actionFields
    }

    const getTableColumns = (fieldId) => {
        const field = formFields.find(f => f.id === fieldId);
        if (field && field.formType === 'tableForm' && field.columns) {
            return field.columns.map((col, index) => ({
                label: col.label || `${t("Column")} ${index + 1}`,
                value: col.id
            }));
        }
        return [];
    }

    return (
        <div style={{ display: "flex", flexDirection: "column", height: "100%", minHeight: "100%" }}>
            <Flex align="center" className='condition-header' gap={8}>
                <ArrowLeftOutlined className="icon-medium" onClick={() => setShowForm(false)} />
                <Typography.Title level={5} className='margin-0'>{t('New Condition')}</Typography.Title>
            </Flex>

            <div style={{ flex: 1, overflowY: "auto", overflowX: "auto" }}>
                <div className='condition-form' key={JSON.stringify(selectedCondition)}>
                    <div style={{ marginBottom: "0.25rem" }}>
                        <div className='condition-form-label'>{t('Condition name')}</div>
                        <Input onChange={handleChange} value={data.name} placeholder={t('Enter condition name')} />
                    </div>
                    <Collapse
                        bordered={false}
                        expandIconPosition="end"
                        style={{
                            border: "1px solid #E8E8FA",
                            borderRadius: "0.75rem",
                            background: "#fff",
                            paddingBottom: "20px"
                        }}
                        defaultActiveKey="1"
                    >
                        <Collapse.Panel
                            key={"1"}
                            className="collapse-panel-section"
                            header={
                                <Typography.Title style={{ margin: 0, padding: 0, textAlign: "left" }} level={5}>
                                    {t('Set the scenario')}
                                </Typography.Title>
                            }
                            showArrow={true}

                        >
                            <div style={{ borderRadius: "0.75rem" }}>
                                <div className='scenario-body'>

                                    {

                                        data.scenarios.map((scenario, scenarioIndex) => {
                                            return (
                                                <div className='action-block' key={"scenario-" + scenarioIndex} style={{ backgroundColor: scenario.conditions?.length > 1 ? "#F7F7F8" : "#FFF", padding: scenario.conditions?.length > 1 ? "0.5rem" : "0" }}>
                                                    {
                                                        scenario.conditions.map((condition, conditionIndex) => {
                                                            return (
                                                                <Flex gap={8} key={`condition-${condition.conditionId || `${scenarioIndex}-${conditionIndex}`}`} align='center' justify='flex-start' style={{ marginBottom: "0.5rem", flexWrap: "wrap" }}>
                                                                    {
                                                                        scenario.conditions.length > 1 && conditionIndex === 0 && <Select placeholder={t("Select field")} options={operators} value={scenario?.operator} onChange={(val) => handleScenarioChange('operator', val, scenarioIndex)} style={{ minWidth: "4.5rem", maxWidth: "4.5rem" }} />
                                                                    }
                                                                    <ReferenceDropdown formElements={formElements} selectedElement={getFormType(condition.reference, condition.parent)} condition={condition} scenarioIndex={scenarioIndex} conditionIndex={conditionIndex} handleScenarioChange={handleScenarioChange} workflowSteps={workflowSteps} />
                                                                    {/* <Select placeholder="Select field" showSearch filterOption={true} optionFilterProp="label" options={formFields.map(field => {
                                                                    return { label: <Tooltip title={field.label}>{field.label}</Tooltip>, value: field.value }
                                                                })} value={condition?.reference} onChange={(val) => handleScenarioChange('reference', val, scenarioIndex, conditionIndex)} className='condition-comparator' /> */}
                                                                    {
                                                                        condition.parent ?
                                                                            <>
                                                                                <Select key={condition.parent} placeholder={t("Comparator") + " - " + condition.comparator} options={getComparators(condition.reference, condition.parent)} value={condition.comparator} onChange={(val) => handleScenarioChange('comparator', val, scenarioIndex, conditionIndex)} style={{ minWidth: "8rem", maxWidth: "10rem", textAlign: "left" }} />
                                                                                <ConditionValue formElements={formElements} selectedElement={getFormType(condition.reference, condition.parent)} condition={condition} scenarioIndex={scenarioIndex} conditionIndex={conditionIndex} handleScenarioChange={handleScenarioChange} workflowSteps={workflowSteps} />
                                                                            </>
                                                                            : null
                                                                    }
                                                                    {(data.scenarios.length > 1 || scenario.conditions.length > 1) && <DeleteOutlined className='text-color-secondary cursor-pointer' style={{ marginLeft: "5px" }} onClick={() => deleteCondition(scenarioIndex, conditionIndex)} />}
                                                                </Flex>
                                                            )
                                                        })
                                                    }
                                                    <div style={{ textAlign: "left" }}>
                                                        <Button size="small" className="condition-button" icon={<PlusOutlined />} onClick={() => addCondition(scenarioIndex)}>{t('Add Condition')}</Button>
                                                    </div>
                                                </div>

                                            )
                                        })
                                    }
                                    <Button style={{ float: "left", borderColor: "#141BCB", color: "#141BCB", padding: "0 0.4375rem", height: "24px" }} icon={<PlusOutlined />} onClick={addScenario}>{t('Add Scenario')}</Button>
                                </div>

                            </div>

                        </Collapse.Panel>
                    </Collapse>
                    <div className='condition-result text-left'>
                        {/* If the above scenario happens, then */}
                        <Typography.Title style={{ margin: 0, padding: 0, textAlign: "left" }} level={5}>
                            {t('Set the action')}
                        </Typography.Title>
                        {
                            actions.map((action, index) => {
                                return <Flex key={"action-" + index} gap={8} align='center' justify='flex-start' style={{ marginBottom: "0.5rem", marginTop: "1rem" }}>
                                    <Select placeholder={t("Select action")} options={condtionActions} value={action.action} onChange={(val) => handleActionChange(val, "action", index, "positive")} style={{ width: "8.5rem" }} />
                                    {
                                        action.action &&
                                        <>
                                            <Select showSearch optionFilterProp='actualLabel' placeholder={t("Select field")} options={getActionFields(action.action)} value={action.field} allowClear onChange={(val) => handleActionChange(val, "field", index, "positive")} style={{ width: "13rem" }} />
                                            {
                                                action.action === "setCellColor" && action.field && getTableColumns(action.field).length > 0 &&
                                                <Select placeholder={t("Select column")} options={getTableColumns(action.field)} value={action.column} onChange={(val) => handleActionChange(val, "column", index, "positive")} style={{ width: "13rem" }} />
                                            }
                                        </>
                                    }
                                    {
                                        action.action === "set" && action.field &&
                                        <SetValue action={action} actionIndex={index} selectedElement={getFormType(action.field)} setElementValue={setElementValue} formElements={formFields} />
                                    }
                                    {
                                        (action.action === "setCellColor" || action.action === "highlight") && action.field &&
                                        <div style={{ marginLeft: "8px" }}>
                                            <ColorPicker
                                                format="hex"
                                                value={action.value || "#1677ff"}
                                                onChange={(color) => setElementValue(typeof color === 'string' ? color : color.toHexString(), index)}
                                                showText
                                            />
                                        </div>
                                    }
                                    {actions.length > 1 && <DeleteOutlined className='text-color-secondary cursor-pointer' onClick={() => deletePositiveAction(index)} />}
                                </Flex>
                            })
                        }
                        <Button size="small" className="condition-button" icon={<PlusOutlined />} onClick={addPositiveAction}>{t('Add Action')}</Button>

                    </div>
                </div>
            </div>
            <div className='condition-actions'>
                <Divider style={{ margin: "20px 0 0 0" }} />
                <div className='flex gap-2' style={{ width: "100%" }}>

                    <Button className='submit-button' onClick={submitConditions}>{t('Save Condition')}</Button>
                    <Button onClick={handleCancel}>{t('Cancel')}</Button>
                </div>
            </div>
        </div>
    )
}

export default AddEditConditionForm
